import { SWRFetcher } from '@commenda/utils';
import useSWR from 'swr';
import { BASE_URL } from './helpers';
import { FormSubmissionsListResponse, GetFormSubmissionsParams } from './types';

/**
 * Hook to fetch form submissions with optional filters and pagination.
 *
 * The API scopes the list to `companyId` for everyone but Commenda admins, so pass `null`
 * to hold the request back until the company is known instead of firing an unscoped one.
 */
export const useFormSubmissions = (
  params?: GetFormSubmissionsParams | null
) => {
  const query = new URLSearchParams();

  // array params (status, formTemplateKey, formTemplateId)
  if (params?.status) {
    params.status.forEach((s) => query.append('status', s));
  }

  if (params?.formTemplateKey) {
    params.formTemplateKey.forEach((k) => query.append('formTemplateKey', k));
  }

  if (params?.formTemplateId) {
    params.formTemplateId.forEach((id) => query.append('formTemplateId', id));
  }

  // userId filter
  if (params?.userId) {
    query.append('userId', params.userId);
  }

  // pagination
  if (params?.limit !== undefined) {
    query.append('limit', params.limit.toString());
  }

  if (params?.offset !== undefined) {
    query.append('offset', params.offset.toString());
  }

  if (params?.companyId !== undefined) {
    query.append('companyId', params.companyId.toString());
  }

  const queryString = query.toString();
  const listUrl = queryString ? `${BASE_URL}?${queryString}` : BASE_URL;
  // A `companyId` of 0 is the placeholder these pages pass while the entity loads. It is never a
  // real company, and the API needs a real one to scope the list, so hold the request back too.
  const url = params === null || params?.companyId === 0 ? null : listUrl;

  const { data, error, isLoading, mutate } =
    useSWR<FormSubmissionsListResponse>(
      url,
      SWRFetcher({
        method: 'GET',
      })
    );

  const totalCount = data?.count ?? 0;

  return {
    data,
    error,
    isLoading,
    mutate,
    submissions: data?.formSubmissions ?? [],
    count: totalCount,
  };
};
